import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Menu as MenuIcon, Moon, Star, Sun } from 'lucide-react';
import { cn } from 'cn';
import { navItemsFor } from 'config/nav';
import type { NavItem } from 'config/nav';
import { Button } from 'components/ui/button';
import { Separator } from 'components/ui/separator';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
    SheetTrigger
} from 'components/ui/sheet';
import { useTheme } from 'lib/theme';
import { useFollowedTeams } from 'lib/followedTeams';
import { useAccountActions, useNavContext } from './useNavContext';

const MAX_NAV_TEAMS = 5;

const linkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
        'block rounded-md px-3 py-2 text-base hover:bg-accent hover:text-accent-foreground',
        isActive && 'bg-accent font-medium text-accent-foreground'
    );

// Below md, everything the desktop top bar and account menu offer lives in one
// slide-in sheet, rendered from the same config/nav.ts entries.
export default function MobileNav() {
    const [open, setOpen] = useState(false);
    const ctx = useNavContext();
    const { signIn, signOut } = useAccountActions();
    const { darkMode, toggleDarkMode } = useTheme();
    const { teams } = useFollowedTeams();
    const navTeams = teams.slice(0, MAX_NAV_TEAMS);
    const close = () => setOpen(false);

    const links = (items: NavItem[]) =>
        items.map((item) => (
            <NavLink key={item.path} to={item.path} className={linkClass} onClick={close}>
                {item.title}
            </NavLink>
        ));

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open menu">
                    <MenuIcon className="size-5" />
                </Button>
            </SheetTrigger>
            <SheetContent side="right" className="gap-0">
                <SheetHeader>
                    <SheetTitle>Menu</SheetTitle>
                    <SheetDescription className="sr-only">Site navigation</SheetDescription>
                </SheetHeader>

                <nav className="flex flex-col gap-1 overflow-y-auto px-2">
                    {links(navItemsFor('primary', ctx))}

                    {ctx.auth.loggedIn && navTeams.length > 0 && (
                        <>
                            <Separator className="my-2" />
                            <p className="px-3 py-1 text-xs font-medium text-muted-foreground uppercase">
                                My teams
                            </p>
                            {navTeams.map((followedTeam) => (
                                <NavLink
                                    key={followedTeam.id}
                                    to={`/team/${followedTeam.team.slug}`}
                                    className={linkClass}
                                    onClick={close}>
                                    <span className="inline-flex items-center gap-1.5">
                                        {followedTeam.isPrimary && (
                                            <Star className="size-3.5 fill-current text-primary" />
                                        )}
                                        {followedTeam.team.name}
                                    </span>
                                </NavLink>
                            ))}
                        </>
                    )}

                    {ctx.auth.loggedIn && (
                        <>
                            <Separator className="my-2" />
                            <p className="px-3 py-1 text-xs font-medium text-muted-foreground uppercase">
                                Account
                            </p>
                            {links(navItemsFor('account', ctx))}
                        </>
                    )}

                    <Separator className="my-2" />
                    {links(navItemsFor('footer', ctx))}
                </nav>

                <SheetFooter className="border-t">
                    <Button variant="outline" onClick={toggleDarkMode}>
                        {darkMode ? <Sun /> : <Moon />}
                        {darkMode ? 'Light mode' : 'Dark mode'}
                    </Button>
                    {!ctx.auth.loading &&
                        (ctx.auth.loggedIn ? (
                            <Button
                                variant="ghost"
                                onClick={() => {
                                    close();
                                    signOut();
                                }}>
                                Sign out
                            </Button>
                        ) : (
                            <Button onClick={signIn}>Sign in</Button>
                        ))}
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
}
